// ================================================================
// src/core/books/remoteSync.js
// ================================================================

import { engine } from './storage.js';
import { api } from './apiClient.js';

const NS = 'edu-books';
const PUSH_DEBOUNCE = 1200;
const POLL_INTERVAL = 5000;   // ۵ ثانیه — برای هم‌زمان‌سازی سریع‌تر بین نقش‌ها


// 🆕 صف حذف — entityهایی که باید از سرور حذف شوند
// ساختار: Array<{ col: string, id: string }>
const pendingDeletions = [];

// 🆕 ثبت listener برای entity:deleted
// (بیرون از attachUser تعریف می‌شود تا مستقل از login کار کند)
engine.on('entity:deleted', (payload) => {
  try {
    if (!payload || !payload.col || !payload.id) return;
    const { col, id } = payload;
    // جلوگیری از تکرار
    if (!pendingDeletions.some((d) => d.col === col && d.id === id)) {
      pendingDeletions.push({ col, id });
    }
    schedulePush();
  } catch (e) {
    console.warn('[sync] entity:deleted handler failed:', e.message);
  }
});



// ─── نگاشت: کلید localStorage → collection سرور ───
const MAP_ENTITIES = {
  [`${NS}:teachers`]:   { col: 'teachers' },
  [`${NS}:requests`]:   { col: 'requests' },
  [`${NS}:projects`]:   { col: 'projects' },
  [`${NS}:project-categories`]: { col: 'projectCategories' }, // 🆕
  [`${NS}:versions`]:   { col: 'versions' },
};

const PREFIX_ENTITIES = [
  { prefix: 'study_active_',          col: 'userStudyActive' },  // 🆕
  { prefix: 'lesson-progress-',       col: 'lessonProgress' },
  { prefix: 'lesson-records-',        col: 'lessonRecords' },
  { prefix: 'step-quiz-progress-',    col: 'stepQuizProgress' },
  { prefix: 'step-quiz-unlock-',      col: 'stepQuizUnlock' },
  { prefix: 'step-quiz-pass-',        col: 'stepQuizPass' },
  { prefix: 'subscription-tx-',       col: 'subscriptionTx' },
  { prefix: 'subscription-',          col: 'subscriptions', exclude: ['subscription-tx-'] },
  { prefix: 'user-stats-',            col: 'userStats' },
  { prefix: 'freestudy-progress-',    col: 'freestudyProgress' },
  { prefix: 'teacher-map-',           col: 'teacherMap' },
  { prefix: 'teacher-ratings-',       col: 'teacherRatings' },
  { prefix: 'video-ratings-',         col: 'videoRatings' },
  { prefix: 'watched-teachers-',      col: 'watchedTeachers' },
  { prefix: 'viewed-videos-',         col: 'viewedVideos' },
  { prefix: 'streak-',                col: 'userStreak' },
  { prefix: 'last-spin-',             col: 'userLastSpin' },
  { prefix: 'user-grade-',            col: 'userGrade' },
  { prefix: 'user-field-',            col: 'userField' },
  { prefix: 'user-library-',          col: 'userLibrary' },
  { prefix: 'user-courses-',          col: 'userCourses' },
  { prefix: 'user-free-study-',       col: 'userFreeStudy' },
  { prefix: 'user-private-class-',    col: 'userPrivateClass' },
  { prefix: 'user-exams-',            col: 'userExams' },
  { prefix: 'workbook-license-',      col: 'workbookLicenses' },
];

// 🆕 ایندکس‌های اشتراک (با prefix مجزا)
const SUBSCRIPTION_USER_PREFIX = 'subscriptions-user-';
const SUBSCRIPTION_PROJECT_PREFIX = 'subscriptions-project-';

const SINGLETON_KEYS = {
  'navbar2-drawer-dark': { col: 'userPrefs', field: 'theme' },
};

const ARRAY_KEYS = {
  [`${NS}:backups`]: { col: 'userBackups' },
};

const LOCAL_ONLY_PREFIXES = [
  'book-total-lessons-v2-',
  `${NS}:__sync__`,
];
const LOCAL_ONLY_KEYS = [
  `${NS}:last-gc`,
  `${NS}:last-cleanup`,
  `${NS}:schema`,
  'project-categories-migrated-v1', // 🆕 فقط محلی
];

const INDEX_KEYS = [
  `${NS}:teacher-by-user`,
  `${NS}:teacher-by-folder`,
  `${NS}:proj-by-teacher`,
  `${NS}:proj-by-key`,
  `${NS}:req-idem`,
  `${NS}:sub-salt`,
];

let currentUserId = null;
let unsubStorage = null;
let pushTimer = null;
let isPushing = false;
let pendingChanges = false;
let initialized = false;
let pollHandle = null;
let unloadHandler = null;
let attachPromise = null;

// 🆕 دفعه ذخیره‌شده (برای قبل از unload)
let lastSavedSnapshot = null;

const serverState = {
  teachers: new Map(), requests: new Map(), projects: new Map(), versions: new Map(),
  notifications: new Map(), auditLog: new Map(),
};

const prefixServerState = {};
for (const p of PREFIX_ENTITIES) prefixServerState[p.col] = new Map();

// 🆕 state ایندکس‌های اشتراک
const subscriptionIndexState = {
  userIndex: new Map(),     // userId → Set<projectId>
  projectIndex: new Map(),  // projectId → Array<{userId, ...}>
};

const _suppress = new Set();

// ─── helpers ───
function isMapKey(key) { return Object.prototype.hasOwnProperty.call(MAP_ENTITIES, key); }
function isSingleton(key) { return Object.prototype.hasOwnProperty.call(SINGLETON_KEYS, key); }
function isArrayKey(key) { return Object.prototype.hasOwnProperty.call(ARRAY_KEYS, key); }
function isIndex(key) { return INDEX_KEYS.includes(key); }
function isLocalOnly(key) {
  return LOCAL_ONLY_KEYS.includes(key) || LOCAL_ONLY_PREFIXES.some((p) => key.startsWith(p));
}
function isSubscriptionIndexKey(key) {
  return key.startsWith(SUBSCRIPTION_USER_PREFIX) || key.startsWith(SUBSCRIPTION_PROJECT_PREFIX);
}
function matchPrefix(key) {
  if (typeof key !== 'string') return null;
  for (const p of PREFIX_ENTITIES) {
    if (p.exclude && p.exclude.some((ex) => key.startsWith(ex))) continue;
    if (key.startsWith(p.prefix)) return p;
  }
  return null;
}

function extractUserId(key) {
  const pats = [
    [/^lesson-progress-(.+?)-/, 1], [/^lesson-records-(.+)$/, 1],
    [/^step-quiz-progress-(.+?)-pj_/, 1], [/^step-quiz-unlock-(.+?)-/, 1],
    [/^step-quiz-pass-(.+?)-/, 1], [/^user-stats-(.+)$/, 1],
    [/^freestudy-progress-(?:v2-)?(.+?)-/, 1], [/^teacher-map-(.+)$/, 1],
    [/^teacher-ratings-(.+)$/, 1], [/^video-ratings-(.+)$/, 1],
    [/^watched-teachers-(.+)$/, 1], [/^viewed-videos-(.+)$/, 1],
    [/^streak-(.+)$/, 1], [/^last-spin-(.+)$/, 1],
    [/^user-grade-(.+)$/, 1], [/^user-field-(.+)$/, 1],
    [/^user-library-(.+)$/, 1], [/^user-courses-(.+)$/, 1],
    [/^user-free-study-(.+)$/, 1], [/^user-private-class-(.+)$/, 1],
    [/^user-exams-(.+)$/, 1],
    [/^subscription-(.+?)-pj_/, 1],
    [/^workbook-license-(.+?)-/, 1],
  ];
  for (const [re, i] of pats) {
    const m = key.match(re);
    if (m) return m[i];
  }
  return null;
}

// ================================================================
// 🆕 اصلاح باگ `_suppress`: خودمان کلید را از suppress پاک می‌کنیم
// ================================================================
function setLocal(key, value, opts = {}) {
  // اگر می‌خواهیم push نشود، suppress را دستی مدیریت می‌کنیم
  if (opts.suppressPush) {
    _suppress.add(key);
    engine.set(key, value, { silent: true });
    // بعد از set، کلید را از suppress حذف می‌کنیم (چون خودمان داده را نوشتیم)
    _suppress.delete(key);
  } else {
    engine.set(key, value, opts);
  }
}

// ================================================================
// PULL
// ================================================================

async function pullMapEntities() {
  for (const [localKey, { col }] of Object.entries(MAP_ENTITIES)) {
    const list = await api.getAll(col);
    if (!Array.isArray(list)) continue;
    const m = new Map();
    for (const e of list) if (e?.id) m.set(e.id, e);
    serverState[col] = m;

    const localMap = engine.get(localKey, {}) || {};
    const serverIds = new Set(m.keys());
    const merged = {};
    const NOW = Date.now();
    const RECENT_THRESHOLD = 2 * 60 * 1000;

    for (const [id, e] of m) {
      const cur = localMap[id];
      if (!cur) { merged[id] = e; continue; }
      const sT = new Date(e.updatedAt || e.createdAt || 0).getTime();
      const lT = new Date(cur.updatedAt || cur.createdAt || 0).getTime();
      if (sT >= lT) merged[id] = e;
      else merged[id] = cur;
    }

    for (const [id, e] of Object.entries(localMap)) {
      if (serverIds.has(id)) continue;
      const lT = new Date(e?.updatedAt || e?.createdAt || 0).getTime();
      if (NOW - lT < RECENT_THRESHOLD) {
        merged[id] = e;
      }
    }

    _suppress.add(localKey);
    engine.set(localKey, merged, { silent: true });
    _suppress.delete(localKey);
  }

  // 🆕 بازسازی ایندکس‌ها بعد از pull
  try {
    const teachersMap = engine.get(`${NS}:teachers`, {}) || {};
    const byUser = {};
    const byFolder = {};
    for (const tp of Object.values(teachersMap)) {
      if (tp?.userId) byUser[tp.userId] = tp.id;
      if (tp?.folderName) byFolder[tp.folderName] = tp.id;
    }
    _suppress.add(`${NS}:teacher-by-user`);
    _suppress.add(`${NS}:teacher-by-folder`);
    engine.set(`${NS}:teacher-by-user`, byUser, { silent: true });
    engine.set(`${NS}:teacher-by-folder`, byFolder, { silent: true });
    _suppress.delete(`${NS}:teacher-by-user`);
    _suppress.delete(`${NS}:teacher-by-folder`);

    const projectsMap = engine.get(`${NS}:projects`, {}) || {};
    const byTeacher = {};
    const byKey = {};
    for (const pj of Object.values(projectsMap)) {
      if (pj?.archivedAt) continue;
      if (pj.teacherId) {
        if (!byTeacher[pj.teacherId]) byTeacher[pj.teacherId] = [];
        byTeacher[pj.teacherId].push(pj.id);
      }
      if (pj.grade && pj.subject && pj.folderName) {
        const key = pj.field
          ? `${pj.grade}-${pj.field}-${pj.subject}-${pj.folderName}`
          : `${pj.grade}-${pj.subject}-${pj.folderName}`;
        byKey[key] = pj.id;
      }
    }
    _suppress.add(`${NS}:proj-by-teacher`);
    _suppress.add(`${NS}:proj-by-key`);
    engine.set(`${NS}:proj-by-teacher`, byTeacher, { silent: true });
    engine.set(`${NS}:proj-by-key`, byKey, { silent: true });
    _suppress.delete(`${NS}:proj-by-teacher`);
    _suppress.delete(`${NS}:proj-by-key`);

    console.log('[sync] ✓ indexes rebuilt');
  } catch (e) {
    console.warn('[sync] index rebuild failed:', e.message);
  }

  // 🆕 بازسازی از طریق dataEngine
  try {
    const { default: dataEngine } = await import('./data.js');
    dataEngine.rebuildAllIndexes?.();
  } catch {}
}

async function pullPrefixEntities() {
  for (const { prefix, col } of PREFIX_ENTITIES) {
    let list = await api.getAll(col, `_userId=${encodeURIComponent(currentUserId)}`);

    if (col === 'subscriptions') {
      const teacherId = engine.get(`${NS}:teacher-by-user`, {})?.[currentUserId];
      if (teacherId) {
        const forTeacher = await api.getAll(col, `teacherId=${encodeURIComponent(teacherId)}`) || [];
        const map = new Map();
        for (const x of (list || [])) if (x?.id) map.set(x.id, x);
        for (const x of forTeacher) if (x?.id) map.set(x.id, x);
        list = Array.from(map.values());
      }
    }

    if (!Array.isArray(list)) continue;
    const m = new Map();
    for (const rec of list) if (rec?.key) m.set(rec.key, rec);
    prefixServerState[col] = m;

    for (const localKey of engine.keys(prefix)) {
      const rec = m.get(localKey);
      if (!rec || rec.data === undefined) continue;
      const localData = engine.get(localKey, null);
      const sT = new Date(rec.updatedAt || 0).getTime();
      const lT = (typeof localData === 'object' && localData?.lastVisitedAt) || 0;
      if (sT >= lT || localData === null) {
        _suppress.add(localKey);
        engine.set(localKey, rec.data, { silent: true });
        _suppress.delete(localKey);
      }
    }
  }

  // 🆕 Pull ایندکس‌های اشتراک
  await pullSubscriptionIndexes();
}

async function pullSubscriptionIndexes() {
  // user index: subscriptions-user-{userId}
  const userKey = `${SUBSCRIPTION_USER_PREFIX}${currentUserId}`;
  const userList = await api.getAll('subscriptions', `userId=${encodeURIComponent(currentUserId)}`);
  if (Array.isArray(userList)) {
    const projectIds = userList.map((s) => s.projectId).filter(Boolean);
    _suppress.add(userKey);
    engine.set(userKey, projectIds, { silent: true });
    _suppress.delete(userKey);
    subscriptionIndexState.userIndex.set(currentUserId, new Set(projectIds));
  }

  // project index: subscriptions-project-{projectId}
  const teacherId = engine.get(`${NS}:teacher-by-user`, {})?.[currentUserId];
  if (teacherId) {
    const allSubs = await api.getAll('subscriptions');
    if (Array.isArray(allSubs)) {
      const byProject = {};
      for (const s of allSubs) {
        if (s.teacherId !== teacherId) continue;
        if (!byProject[s.projectId]) byProject[s.projectId] = [];
        byProject[s.projectId].push({
          userId: s.userId,
          expiresAt: s.expiresAt,
          status: s.status,
          amount: s.amount,
        });
      }
      for (const [pid, arr] of Object.entries(byProject)) {
        const key = `${SUBSCRIPTION_PROJECT_PREFIX}${pid}`;
        _suppress.add(key);
        engine.set(key, arr, { silent: true });
        _suppress.delete(key);
        subscriptionIndexState.projectIndex.set(pid, arr);
      }
    }
  }
}

async function pullSingletons() {
  const list = await api.getAll('userPrefs', `id=${encodeURIComponent(currentUserId)}`);
  const rec = Array.isArray(list) ? list[0] : null;
  if (rec) {
    for (const [localKey, { field }] of Object.entries(SINGLETON_KEYS)) {
      if (rec[field] !== undefined) {
        _suppress.add(localKey);
        try { localStorage.setItem(localKey, String(rec[field])); } catch {}
        _suppress.delete(localKey);
      }
    }
  }
}

async function pullArrayKeys() {
  for (const [localKey, { col }] of Object.entries(ARRAY_KEYS)) {
    const list = await api.getAll(col, `id=${encodeURIComponent(currentUserId)}`);
    const rec = Array.isArray(list) ? list[0] : null;
    if (rec && Array.isArray(rec.data)) {
      _suppress.add(localKey);
      engine.set(localKey, rec.data, { silent: true });
      _suppress.delete(localKey);
    }
  }
}

async function pullNotificationsAndAudit() {
  // 🆕 فیلتر اعلان‌هایی که در صف حذف هستند
  const pendingDelIds = new Set(
    pendingDeletions.filter((d) => d.col === 'notifications').map((d) => d.id)
  );

  const notifs = await api.getAll('notifications', `recipientId=${encodeURIComponent(currentUserId)}`);
  if (Array.isArray(notifs)) {
    const filtered = notifs.filter((n) => n?.id && !pendingDelIds.has(n.id));

    const sm = new Map();
    for (const n of filtered) if (n?.id) sm.set(n.id, n);
    serverState.notifications = sm;

    const localMap = engine.get(`${NS}:notifs`, {}) || {};
    const currentLocal = Array.isArray(localMap[currentUserId]) ? localMap[currentUserId] : [];
    const localById = new Map(currentLocal.map((n) => [n.id, n]));
    const serverIds = new Set();
    const merged = [];

    // 🆕 Merge هوشمند — حفظ وضعیت "خوانده شده" لوکال
    for (const serverN of filtered) {
      serverIds.add(serverN.id);
      const localN = localById.get(serverN.id);
      if (!localN) {
        merged.push(serverN);
      } else if (localN.read && !serverN.read) {
        // لوکال "خوانده" بود، سرور "نخوانده" → لوکال را نگه‌دار
        merged.push(localN);
      } else if ((localN.readAt || 0) > (serverN.readAt || 0)) {
        merged.push(localN);
      } else {
        merged.push(serverN);
      }
    }

    // حفظ اعلان‌های لوکال بسیار جدید که هنوز push نشده‌اند
    const nowMs = Date.now();
    for (const n of currentLocal) {
      if (!serverIds.has(n.id) && !pendingDelIds.has(n.id)) {
        if (nowMs - (n.createdAt || 0) < 2 * 60 * 1000) {
          merged.push(n);
        }
      }
    }

    merged.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    const finalMap = { ...localMap };
    finalMap[currentUserId] = merged;

    _suppress.add(`${NS}:notifs`);
    engine.set(`${NS}:notifs`, finalMap, { silent: true });
    _suppress.delete(`${NS}:notifs`);
  }

  const audit = await api.getAll('auditLog');
  if (Array.isArray(audit)) {
    const sm = new Map();
    for (const a of audit) if (a?.id) sm.set(a.id, a);
    serverState.auditLog = sm;

    const local = engine.get(`${NS}:audit`, []) || [];
    const serverIds = new Set(audit.map((a) => a.id));
    const localOnly = local.filter((a) => a?.id && !serverIds.has(a.id));
    const merged = [...audit, ...localOnly].sort((a, b) => (a.at || 0) - (b.at || 0));
    _suppress.add(`${NS}:audit`);
    engine.set(`${NS}:audit`, merged, { silent: true });
    _suppress.delete(`${NS}:audit`);
  }
}

async function pullSalt() {
  try {
    const list = await api.getAll('platformConfig');
    const cfg = (list || []).find((c) => c.key === 'sub-salt');
    if (cfg?.value) {
      _suppress.add(`${NS}:sub-salt`);
      engine.set(`${NS}:sub-salt`, cfg.value, { silent: true });
      _suppress.delete(`${NS}:sub-salt`);
      try {
        const { subscriptionManager } = await import('./logic.js');
        subscriptionManager.invalidateSaltCache();
      } catch {}
      return;
    }
    const salt = `sub_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    await api.post('platformConfig', { key: 'sub-salt', value: salt, createdAt: Date.now() });
    _suppress.add(`${NS}:sub-salt`);
    engine.set(`${NS}:sub-salt`, salt, { silent: true });
    _suppress.delete(`${NS}:sub-salt`);
  } catch (e) {
    console.warn('[sync] salt:', e.message);
  }
}

async function pullAll() {
  try {
    await pullMapEntities();
    await pullPrefixEntities();
    await pullSingletons();
    await pullArrayKeys();
    await pullNotificationsAndAudit();
    await pullSalt();

    engine.emit('registry:teacher:changed',  { key: 'pull' });
    engine.emit('registry:request:changed',  { key: 'pull' });
    engine.emit('registry:project:changed',  { key: 'pull' });
    engine.emit('registry:version:changed',  { key: 'pull' });
    engine.emit('data:changed',              { key: 'pull' });
  } catch (e) {
    console.warn('[sync] pullAll error:', e.message);
  }
}

// ================================================================
// PUSH
// ================================================================

async function pushMap(localKey, col) {
  const localMap = engine.get(localKey, {}) || {};
  const serverMap = serverState[col] || new Map();

  for (const [id, e] of Object.entries(localMap)) {
    if (!e) continue;
    const withId = { ...e, id };

    if (serverMap.has(id)) {
      const se = serverMap.get(id);
      const sT = new Date(se.updatedAt || se.createdAt || 0).getTime();
      const lT = new Date(e.updatedAt || e.createdAt || 0).getTime();
      if (lT > sT) {
        const ok = await api.put(col, id, withId);
        if (ok) serverMap.set(id, withId);
      }
    } else {
      // ✅ PUT upsert به‌جای POST — id کلاینت هرگز از بین نمی‌رود
      const ok = await api.put(col, id, withId);
      if (ok) serverMap.set(id, withId);
    }
  }
}

async function pushPrefix(col) {
  const p = PREFIX_ENTITIES.find((x) => x.col === col);
  if (!p) return;
  const serverMap = prefixServerState[col] || new Map();

  for (const localKey of engine.keys(p.prefix)) {
    if (p.exclude && p.exclude.some((ex) => localKey.startsWith(ex))) continue;
    const data = engine.get(localKey, null);
    if (data === null) continue;

    const userId = extractUserId(localKey);
    if (userId && currentUserId && userId !== currentUserId) continue;

    const existing = serverMap.get(localKey);
    const flatData = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};

    const payload = {
      ...flatData,
      id: localKey,
      key: localKey,
      _userId: userId || flatData.userId || null,
      updatedAt: Date.now(),
      data,
    };

    // ✅ PUT upsert با localKey به‌عنوان id — id همیشه حفظ می‌شود
    const ok = await api.put(col, localKey, payload);
    if (ok) serverMap.set(localKey, payload);
  }
}

// 🆕 push ایندکس‌های اشتراک
async function pushSubscriptionIndexes() {
  // user index
  const userKey = `${SUBSCRIPTION_USER_PREFIX}${currentUserId}`;
  const localUserIndex = engine.get(userKey, []);
  if (Array.isArray(localUserIndex) && localUserIndex.length > 0) {
    // این ایندکس روی سرور ذخیره نمی‌شود (از subscriptions مشتق می‌شود)
    // فقط برای اطمینان، به عنوان یک رکورد در subscriptions ذخیره نمی‌کنیم
  }
}

async function pushSingletons() {
  if (!currentUserId) return;
  const existingList = await api.getAll('userPrefs', `id=${encodeURIComponent(currentUserId)}`);
  const existing = Array.isArray(existingList) ? existingList[0] : null;

  const payload = { id: currentUserId, userId: currentUserId, updatedAt: Date.now() };
  for (const [localKey, { field }] of Object.entries(SINGLETON_KEYS)) {
    try {
      // 🆕 خواندن از engine (نه localStorage مستقیم) — هماهنگ با شعار «همه چیز از engine»
      const v = engine.getRaw(localKey);
      if (v !== null && v !== undefined) payload[field] = v;
    } catch {}
  }

  // ✅ PUT upsert (create-or-update)
  await api.put('userPrefs', currentUserId, payload);
}

async function pushArrayKeys() {
  if (!currentUserId) return;
  for (const [localKey, { col }] of Object.entries(ARRAY_KEYS)) {
    const data = engine.get(localKey, null);
    if (!Array.isArray(data)) continue;

    const existingList = await api.getAll(col, `id=${encodeURIComponent(currentUserId)}`);
    const existing = Array.isArray(existingList) ? existingList[0] : null;

    const payload = { id: currentUserId, userId: currentUserId, data, updatedAt: Date.now() };
    // ✅ PUT upsert
    await api.put(col, currentUserId, payload);
  }
}

async function pushNotifications() {
  const localMap = engine.get(`${NS}:notifs`, {}) || {};
  const serverMap = serverState.notifications;

  // 🆕 جمع‌آوری همه اعلان‌ها (نه فقط اعلان‌های خودم)
  const flat = [];
  const localIds = new Set();
  for (const arr of Object.values(localMap)) {
    if (Array.isArray(arr)) for (const n of arr) {
      if (n?.id) { flat.push(n); localIds.add(n.id); }
    }
  }

  // 🆕 push همه اعلان‌ها (نه فقط اعلان‌های recipientId === currentUserId)
  for (const n of flat) {
    // اعلان‌هایی که قبلاً در سرور هستند را آپدیت کن
    // ✅ PUT upsert همیشه
    const ok = await api.put('notifications', n.id, n);
    if (ok) serverMap.set(n.id, n);
  }

  // حذف اعلان‌های حذف‌شده
  for (const id of Array.from(serverMap.keys())) {
    if (!localIds.has(id)) {
      await api.del('notifications', id);
      serverMap.delete(id);
    }
  }
}

async function pushAudit() {
  const local = engine.get(`${NS}:audit`, []) || [];
  const serverMap = serverState.auditLog;
  for (const a of local.slice(-500)) {
    if (!a?.id) continue;
    if (!serverMap.has(a.id)) {
      // ✅ PUT upsert
      const ok = await api.put('auditLog', a.id, a);
      if (ok) serverMap.set(a.id, a);
    }
  }
}

async function pushAll() {
  if (!currentUserId) return;
  if (isPushing) { pendingChanges = true; return; }
  isPushing = true;
  try {
    // 🆕 اول حذف‌ها را از سرور حذف کن (قبل از push)
    await processPendingDeletions();

    for (const [localKey, { col }] of Object.entries(MAP_ENTITIES)) {
      await pushMap(localKey, col);
    }
    for (const { col } of PREFIX_ENTITIES) {
      await pushPrefix(col);
    }
    await pushSubscriptionIndexes();
    await pushSingletons();
    await pushArrayKeys();
    await pushNotifications();
    await pushAudit();
    console.log('[sync] ✓ pushed all');
  } catch (e) {
    console.warn('[sync] push error:', e.message);
  } finally {
    isPushing = false;
    if (pendingChanges) { pendingChanges = false; schedulePush(); }
  }
}

// 🆕 پردازش صف حذف — اتمیک با api.delSafe (404 = موفقیت)
async function processPendingDeletions() {
  if (pendingDeletions.length === 0) return;
  const snapshot = pendingDeletions.slice();
  const remaining = [];
  let deleted = 0;

  for (const { col, id } of snapshot) {
    try {
      const ok = await api.delSafe(col, id);
      if (ok) {
        deleted++;
        // همگام‌سازی server state
        const sm = serverState[col];
        if (sm) sm.delete(id);
        const pm = prefixServerState[col];
        if (pm) pm.delete(id);
      } else {
        // فقط 404 یا خطاهای گذرا — در صف می‌ماند تا دور بعد
        remaining.push({ col, id });
      }
    } catch (e) {
      remaining.push({ col, id });
    }
  }

  pendingDeletions.length = 0;
  pendingDeletions.push(...remaining);

  if (deleted > 0) {
    console.log(`[sync] ✓ deleted ${deleted} entities from server (${remaining.length} still pending)`);
  }
}

function schedulePush() {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(pushAll, PUSH_DEBOUNCE);
}

function onOnline() { if (currentUserId) schedulePush(); }

// ================================================================
// POLLING
// ================================================================
function startPolling() {
  stopPolling();
  let lastCheck = 0;

  const POLL_COLLECTIONS = ['requests', 'projects', 'teachers'];

  async function checkOne(col) {
    try {
      const list = await api.getAll(col);
      if (!Array.isArray(list)) return false;

      const serverIds = new Set();
      for (const e of list) {
        if (!e?.id) continue;
        serverIds.add(e.id);
      }

      const local = engine.get(`${NS}:${col}`, {}) || {};
      const localCount = Object.keys(local).length;

      if (localCount !== serverIds.size) return true;

      for (const e of list) {
        if (!e?.id) continue;
        const cur = local[e.id];
        if (!cur) return true;
        const sT = new Date(e.updatedAt || e.createdAt || 0).getTime();
        const lT = new Date(cur.updatedAt || cur.createdAt || 0).getTime();
        if (sT > lT) return true;
      }

      return false;
    } catch { return false; }
  }

  const check = async () => {
    if (!currentUserId) return;
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastCheck < POLL_INTERVAL) return;
    lastCheck = now;
    try {
      let changed = false;

      for (const col of POLL_COLLECTIONS) {
        if (await checkOne(col)) { changed = true; break; }
      }

      if (!changed) {
        try {
          const notifs = await api.getAll(
            'notifications',
            `recipientId=${encodeURIComponent(currentUserId)}`
          );
          if (Array.isArray(notifs)) {
            const localNotifs = engine.get(`${NS}:notifs`, {})?.[currentUserId] || [];
            if (localNotifs.length !== notifs.length) changed = true;
            else {
              for (const n of notifs) {
                const cur = localNotifs.find((x) => x?.id === n.id);
                if (!cur) { changed = true; break; }
              }
            }
          }
        } catch {}
      }

      if (changed) {
        console.log('[sync] polling: change detected');
        await pullAll();
      }
    } catch {}
  };

  const onVisible = () => { if (!document.hidden) check(); };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', check);

  const intervalHandle = setInterval(check, POLL_INTERVAL);

  pollHandle = {
    stop: () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      clearInterval(intervalHandle);
    },
  };
}

function stopPolling() {
  if (pollHandle?.stop) { try { pollHandle.stop(); } catch {} }
  pollHandle = null;
}

// ================================================================
// Public API
// ================================================================


// ================================================================
// 🆕 بازیابی pending queue از localStorage (بعد از unload یا crash)
// ================================================================
// ================================================================
// 🆕 بازیابی pending queue از localStorage (بعد از unload یا crash)
// ================================================================
async function restorePendingQueue(userId) {
  try {
    const raw = localStorage.getItem(`${NS}:__pending_push__`);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId || !Array.isArray(parsed.queue)) {
      localStorage.removeItem(`${NS}:__pending_push__`);
      return;
    }

    // اگر queue از ۱۰ دقیقه قبل قدیمی‌تره، نادیده بگیر
    if (Date.now() - (parsed.at || 0) > 10 * 60 * 1000) {
      localStorage.removeItem(`${NS}:__pending_push__`);
      return;
    }

    console.log(`[sync] restoring ${parsed.queue.length} pending items...`);

    for (const item of parsed.queue) {
      try {
        if (item.op === 'post') {
          await api.post(item.col, item.body);
        } else if (item.op === 'put') {
          await api.put(item.col, item.id, item.body);
        }
      } catch (e) {
        console.warn(`[sync] restore item failed (${item.op} ${item.col}):`, e.message);
      }
    }

    localStorage.removeItem(`${NS}:__pending_push__`);
    console.log('[sync] ✓ pending queue restored');
  } catch (e) {
    console.warn('[sync] restorePendingQueue error:', e.message);
  }
}





// 🆕 وضعیت sync برای diagnostic
export const syncState = {
  attachedUserId: null,
  lastPullAt: null,
  lastPushAt: null,
  lastPullError: null,
  lastPushError: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
};







export async function attachUser(userId) {
  if (!userId) return;
  if (currentUserId === userId && attachPromise) return attachPromise;
  currentUserId = userId;

  attachPromise = (async () => {
    // 🆕 اول صف حذف را از نشست قبلی بازیابی کن (قبل از pull)
    try {
      const raw = localStorage.getItem(`${NS}:__pending_deletions__`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.userId === userId && Date.now() - (parsed.at || 0) < 10 * 60 * 1000) {
          for (const d of parsed.queue || []) {
            if (d.col && d.id && !pendingDeletions.some((x) => x.col === d.col && x.id === d.id)) {
              pendingDeletions.push({ col: d.col, id: d.id });
            }
          }
        }
        localStorage.removeItem(`${NS}:__pending_deletions__`);
      }
    } catch (e) {
      console.warn('[sync] restore pending deletions failed:', e.message);
    }

    // حالا pull کن — pendingDeletions فیلتر می‌کند
    try { await pullAll(); } catch (e) { console.warn('[sync] pull:', e); }

    // ... (بقیه setup بدون تغییر) ...

    // 🆕 اگر صف حذف پر است، بلافاصله push کن
    if (pendingDeletions.length > 0) schedulePush();

    if (unsubStorage) { try { unsubStorage(); } catch {} }
    unsubStorage = engine.on('storage:write', ({ key }) => {
      if (_suppress.has(key)) { _suppress.delete(key); return; }
      if (isIndex(key)) return;
      if (isLocalOnly(key)) return;
      if (isMapKey(key) || matchPrefix(key) || isSingleton(key) || isArrayKey(key) ||
          key === `${NS}:notifs` || key === `${NS}:audit` ||
          isSubscriptionIndexKey(key)) {
        schedulePush();
      }
    });

    const directStorageListener = (e) => {
      if (!e.key) return;
      if (isSingleton(e.key)) schedulePush();
    };
    window.addEventListener('storage', directStorageListener);

    // 🆕 unload: flush همه‌ی pending push ها قبل از بستن
    // ----------------------------------------------------------------
    // چرا؟ چون schedulePush با debounce کار می‌کند؛ اگر کاربر سریع
    // صفحه را ببندد، داده در صف می‌ماند و از دست می‌رود.
    // راهکار:
    //   ۱. ذخیره‌ی queue در localStorage با کلید __pending_push__
    //   ۲. flush فوری روی visibilitychange=hidden (sync)
    //   ۳. گوش به pagehide هم (iOS Safari فقط این را fire می‌کند)
    // ----------------------------------------------------------------
    unloadHandler = () => {
      try {
        if (!currentUserId) return;

        // اگر صف خالی است، کاری نکن
        if (!pendingChanges && !pushTimer) return;

        // ساخت queue از diff بین localStorage و آخرین state سرور
        const pendingQueue = [];

        // ۱. MAP_ENTITIES: teachers, requests, projects, versions
        for (const [localKey, { col }] of Object.entries(MAP_ENTITIES)) {
          const localMap = engine.get(localKey, {}) || {};
          const serverMap = serverState[col] || new Map();
          for (const [id, e] of Object.entries(localMap)) {
            if (!e) continue;
            const se = serverMap.get(id);
            const withId = { ...e, id };
            if (!se) {
              pendingQueue.push({ op: 'post', col, body: withId });
            } else {
              const sT = new Date(se.updatedAt || se.createdAt || 0).getTime();
              const lT = new Date(e.updatedAt || e.createdAt || 0).getTime();
              if (lT > sT) {
                pendingQueue.push({ op: 'put', col, id, body: withId });
              }
            }
          }
        }

        // ۲. PREFIX_ENTITIES: lesson-progress, step-quiz-progress, ...
        for (const p of PREFIX_ENTITIES) {
          const serverMap = prefixServerState[p.col] || new Map();
          for (const localKey of engine.keys(p.prefix)) {
            if (p.exclude && p.exclude.some((ex) => localKey.startsWith(ex))) continue;
            const data = engine.get(localKey, null);
            if (data === null) continue;

            const userId = extractUserId(localKey);
            if (userId && userId !== currentUserId) continue;

            const existing = serverMap.get(localKey);
            const flatData = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
            const payload = {
              ...flatData,
              id: localKey,
              key: localKey,
              _userId: userId || flatData.userId || null,
              updatedAt: Date.now(),
              data,
            };

            if (existing?.id) {
              pendingQueue.push({ op: 'put', col: p.col, id: existing.id, body: payload });
            } else {
              pendingQueue.push({ op: 'post', col: p.col, body: payload });
            }
          }
        }

        // ۳. ذخیره‌ی queue در localStorage برای بازیابی در بارگذاری بعدی
        if (pendingQueue.length > 0) {
          try {
            localStorage.setItem(`${NS}:__pending_push__`, JSON.stringify({
              userId: currentUserId,
              at: Date.now(),
              queue: pendingQueue.slice(0, 500),
            }));
          } catch {}
        }
      } catch (e) {
        console.warn('[sync] unloadHandler failed:', e);
      }

      // 🆕 ذخیره‌ی صف حذف (مستقل از queue بالا، چون در detachUser هم فراخوانی می‌شود)
      try {
        if (currentUserId && pendingDeletions.length > 0) {
          localStorage.setItem(`${NS}:__pending_deletions__`, JSON.stringify({
            userId: currentUserId,
            at: Date.now(),
            queue: pendingDeletions.slice(0, 200),
          }));
        }
      } catch {}
    };
    window.addEventListener('beforeunload', unloadHandler);
    window.addEventListener('pagehide', unloadHandler);

    // 🆕 visibilitychange: flush فوری (بدون debounce)
    // چرا؟ چون کاربر ممکن است تب را عوض کند و هیچ‌وقت برنگردد.
    // در این حالت beforeunload fire نمی‌شود و داده از دست می‌رود.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // ۱. queue را در localStorage ذخیره کن (به عنوان backup)
        unloadHandler();

        // ۲. push فوری — fire and forget
        if (pushTimer) {
          clearTimeout(pushTimer);
          pushTimer = null;
        }
        pushAll().catch((e) => console.warn('[sync] visibility flush failed:', e));
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    window.addEventListener('online', onOnline);
    startPolling();
    schedulePush();

    // 🆕 بازیابی pending queue از نشست قبلی
    // اگر در unloadHandler queue را ذخیره کرده بودیم، الان می‌فرستیم
    // 🆕 بازیابی pending queue از نشست قبلی
    try {
      await restorePendingQueue(userId);
    } catch (e) {
      console.warn('[sync] restore pending queue failed:', e);
    }



    console.log(`[sync] attached ${userId}`);
  })();

  return attachPromise;
}

export async function detachUser() {
  if (!currentUserId) return;
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
  window.removeEventListener('online', onOnline);
  if (unloadHandler) { try { window.removeEventListener('beforeunload', unloadHandler); } catch {} unloadHandler = null; }
  stopPolling();
  if (unsubStorage) { try { unsubStorage(); } catch {} unsubStorage = null; }
  try { await pushAll(); } catch {}
  // 🆕 صف حذف را حفظ کن (در صورت logout، بعد از login بعدی restore می‌شود)
  try {
    if (pendingDeletions.length > 0) {
      localStorage.setItem(`${NS}:__pending_deletions__`, JSON.stringify({
        userId: currentUserId,
        at: Date.now(),
        queue: pendingDeletions.slice(0, 200),
      }));
    }
  } catch {}
  currentUserId = null;
  attachPromise = null;
}

export async function flushNow() {
  if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
  await pushAll();
}

export function attachSyncBridge(engineInstance) {
  if (initialized) return;
  initialized = true;

  // ✅ مسیر ۱: event (اگر AuthContext در آینده emit کند)
  engineInstance.on('auth:user-changed', ({ userId }) => {
    if (userId) attachUser(userId).catch((e) => console.warn('[sync] attach failed:', e));
    else detachUser().catch(() => {});
  });

  // ✅ مسیر ۲: از localStorage اولیه (برای refresh صفحه)
  // اگر AuthContext دیر برسد یا نرسد، این تضمین می‌کند sync راه بیفتد
  try {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      attachUser(storedUserId).catch((e) =>
        console.warn('[sync] initial attach from localStorage failed:', e)
      );
    }
  } catch {}

  console.log('[sync] bridge attached');
}